import { useEffect } from "react";
import { getReviewCount, syncReviewStats } from "../lib/reviewStats";

/**
 * Update document.title, meta description, canonical and a per-page JSON-LD.
 * Cleans up the JSON-LD on unmount so each route only injects its own.
 */
export default function useSEO({ title, description, canonical, jsonLd, noindex = false }) {
  useEffect(() => {
    if (title) document.title = title;

    if (description) {
      let meta = document.querySelector('meta[name="description"]');
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute("name", "description");
        document.head.appendChild(meta);
      }
      meta.setAttribute("content", description);
    }

    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonical);
    }

    // robots noindex (for 404 / private routes)
    let robotsMeta = document.querySelector('meta[name="robots"]');
    if (noindex) {
      if (!robotsMeta) {
        robotsMeta = document.createElement("meta");
        robotsMeta.setAttribute("name", "robots");
        robotsMeta.setAttribute("data-managed-by-seo", "true");
        document.head.appendChild(robotsMeta);
      }
      robotsMeta.setAttribute("content", "noindex, follow");
    } else if (robotsMeta && robotsMeta.getAttribute("data-managed-by-seo") === "true") {
      robotsMeta.parentNode.removeChild(robotsMeta);
    }

    // OG title/desc
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle && title) ogTitle.setAttribute("content", title);
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc && description) ogDesc.setAttribute("content", description);
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl && canonical) ogUrl.setAttribute("content", canonical);

    // Twitter title/desc (kept in sync with page title/description)
    const twTitle = document.querySelector('meta[name="twitter:title"]');
    if (twTitle && title) twTitle.setAttribute("content", title);
    const twDesc = document.querySelector('meta[name="twitter:description"]');
    if (twDesc && description) twDesc.setAttribute("content", description);

    let scriptEl;
    if (jsonLd) {
      scriptEl = document.createElement("script");
      scriptEl.type = "application/ld+json";
      scriptEl.setAttribute("data-page-jsonld", "true");
      scriptEl.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(scriptEl);
    }

    // Review stars eligibility: per-page Product JSON-LD with aggregateRating.
    // Google blocks stars for self-serving LocalBusiness/MovingCompany ratings,
    // but Product markup on inner pages remains eligible. Never on homepage.
    let ratingEl;
    const isHomepage = !canonical || canonical.replace(/\/$/, "") === "https://bigdemenagement.fr";
    if (!noindex && !isHomepage) {
      ratingEl = document.createElement("script");
      ratingEl.type = "application/ld+json";
      ratingEl.setAttribute("data-rating-jsonld", "true");
      ratingEl.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Product",
        name: title || "Déménagement — Big Déménagement",
        description: description || "Service de déménagement professionnel à Paris et en Île-de-France.",
        image: "https://bigdemenagement.fr/og-big-demenagement.jpg",
        url: canonical,
        brand: { "@type": "Brand", name: "Big Déménagement" },
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.9",
          reviewCount: String(getReviewCount()),
          bestRating: "5",
          worstRating: "1",
        },
      });
      document.head.appendChild(ratingEl);
    }

    // Synchronise le reviewCount de tous les JSON-LD avec le live Google.
    syncReviewStats();

    return () => {
      if (scriptEl && scriptEl.parentNode) {
        scriptEl.parentNode.removeChild(scriptEl);
      }
      if (ratingEl && ratingEl.parentNode) {
        ratingEl.parentNode.removeChild(ratingEl);
      }
    };
  }, [title, description, canonical, jsonLd, noindex]);
}
